import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, DollarSign, TrendingUp, Users, Percent } from "lucide-react";

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Math.max(0, Math.round(n)));

const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Math.round(n));

// Industry assumption: consistent dealer follow-up + status tracking lifts
// closed-won by ~35% on inquiries that would otherwise go dark. Kept
// conservative and clearly labeled in-UI so numbers stay credible.
const UPLIFT_PCT = 0.35;

export default function RoiCalculator() {
  const [leads, setLeads] = useState(120);       // contractor inquiries / month
  const [aov, setAov] = useState(3200);          // avg order value, USD
  const [closeRate, setCloseRate] = useState(15);// current close rate, %

  const { monthlyUplift, annualUplift, leadsRecovered, todayRevenue, withRevenue } = useMemo(() => {
    const closed = leads * (closeRate / 100);
    const recovered = closed * UPLIFT_PCT;
    const today = closed * aov;
    const withLL = today + recovered * aov;
    return {
      monthlyUplift: recovered * aov,
      annualUplift: recovered * aov * 12,
      leadsRecovered: recovered,
      todayRevenue: today,
      withRevenue: withLL,
    };
  }, [leads, aov, closeRate]);

  return (
    <section id="roi" className="bg-[#0B1F33] text-white py-24 border-t border-[#17324D] scroll-mt-24" data-testid="roi-calculator">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C62828]" style={{ animation: "sol-blink 1.4s ease-in-out infinite" }}/>
            <span className="font-mono-ll text-[10px] tracking-[0.28em] uppercase text-slate-300 font-semibold">The Numbers · ROI Estimator</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl mt-5 leading-[1.05] !text-white">
            How much revenue are you leaving <span className="text-[#C62828]">on the table?</span>
          </h2>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto font-body">
            Plug in your numbers. We&apos;ll estimate the annual revenue LumberLoop USA can recover from inquiries your dealer network is already receiving.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8 items-stretch">
          {/* INPUTS */}
          <div className="lg:col-span-7 rounded-2xl bg-white/[0.04] border border-white/10 p-7 md:p-9 space-y-8" data-testid="roi-inputs">
            <SliderRow
              icon={Users}
              label="Contractor inquiries per month"
              hint="Quote requests, product questions, dealer look-ups on your site."
              value={leads}
              setValue={setLeads}
              min={20} max={1000} step={10}
              format={fmtInt}
              testid="roi-leads"
            />
            <SliderRow
              icon={DollarSign}
              label="Average order value"
              hint="Typical first-order size when a contractor becomes a customer."
              value={aov}
              setValue={setAov}
              min={500} max={25000} step={100}
              format={fmtUSD}
              testid="roi-aov"
            />
            <SliderRow
              icon={Percent}
              label="Current dealer close rate"
              hint="Percent of routed inquiries that become closed orders today."
              value={closeRate}
              setValue={setCloseRate}
              min={3} max={50} step={1}
              format={(v) => `${v}%`}
              testid="roi-close"
            />
            <div className="text-[11px] font-mono-ll tracking-widest uppercase text-slate-500 pt-2 border-t border-white/10">
              Assumes a {Math.round(UPLIFT_PCT * 100)}% uplift on close rate when every routed lead is tracked, followed up on, and status-updated by the dealer.
            </div>
          </div>

          {/* OUTPUT */}
          <div className="lg:col-span-5 rounded-2xl bg-gradient-to-br from-[#C62828] to-[#8f1c1c] p-7 md:p-9 flex flex-col justify-between shadow-[0_20px_60px_-20px_rgba(198,40,40,0.55)]" data-testid="roi-output">
            <div>
              <div className="font-mono-ll text-[10px] tracking-[0.3em] uppercase text-white/80">Estimated annual recovered revenue</div>
              <div className="mt-3 font-display text-5xl md:text-6xl lg:text-7xl leading-none !text-white tabular-nums" data-testid="roi-annual-value">
                {fmtUSD(annualUplift)}
              </div>
              <div className="mt-3 text-sm text-white/85 font-body">
                ≈ {fmtUSD(monthlyUplift)} / month · {fmtInt(leadsRecovered)} additional closed orders / month
              </div>

              <div className="mt-8 grid grid-cols-2 gap-3">
                <StatChip icon={TrendingUp} label="Annual revenue today" value={fmtUSD(todayRevenue * 12)} testid="roi-today"/>
                <StatChip icon={TrendingUp} label="With LumberLoop" value={fmtUSD(withRevenue * 12)} accent testid="roi-with"/>
              </div>
            </div>

            <Link to="/contact" className="btn-cta !py-3 mt-8 justify-center" data-testid="roi-cta">
              Show me these numbers on my dashboard <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderRow({ icon: Icon, label, hint, value, setValue, min, max, step, format, testid }) {
  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#C62828]/15 text-[#C62828] flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon size={16}/>
          </div>
          <div>
            <div className="font-heading !text-white text-lg leading-tight">{label}</div>
            <div className="text-xs text-slate-400 mt-1 font-body">{hint}</div>
          </div>
        </div>
        <div className="font-display text-2xl md:text-3xl !text-white tabular-nums whitespace-nowrap" data-testid={`${testid}-value`}>
          {format(value)}
        </div>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        aria-label={label}
        aria-valuetext={typeof value === "number" ? `${format(value)}` : undefined}
        className="w-full mt-4 accent-[#C62828] cursor-pointer"
        data-testid={testid}
      />
    </div>
  );
}

function StatChip({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className={`rounded-lg px-3 py-2.5 ${accent ? "bg-white text-[#C62828]" : "bg-white/10 text-white"}`} data-testid={testid}>
      <div className={`flex items-center gap-1.5 font-mono-ll text-[9px] tracking-[0.24em] uppercase ${accent ? "text-[#C62828]/80" : "text-white/70"}`}>
        <Icon size={10}/> {label}
      </div>
      <div className={`font-display text-xl mt-1 tabular-nums leading-none ${accent ? "!text-[#C62828]" : "!text-white"}`}>{value}</div>
    </div>
  );
}
